<template>
  <div class="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-purple-50">
    <div class="max-w-4xl mx-auto px-4 py-20">
      <!-- Header -->
      <div class="text-center mb-12">
        <NuxtLink to="/" class="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Home
        </NuxtLink>
        
        <h1 class="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-red-600 to-pink-600">
          Request a Demo
        </h1>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          See how Peachy Pro Studios can transform your content production workflow. Let's schedule a personalized demo.
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="submitted" class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div class="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h2 class="text-3xl font-bold mb-4 text-gray-900">Thank You!</h2>
        <p class="text-lg text-gray-600 mb-8">
          We've received your demo request. Our team will reach out to you within 24 hours to schedule your personalized demo.
        </p>
        <NuxtLink to="/" class="inline-flex items-center justify-center bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
          Return to Home
        </NuxtLink>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="handleSubmit" class="bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Full Name -->
          <div class="md:col-span-2">
            <label for="fullName" class="block text-sm font-semibold text-gray-700 mb-2">
              Full Name <span class="text-red-500">*</span>
            </label>
            <input
              id="fullName"
              v-model="formData.fullName"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="John Doe"
            />
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
              Work Email <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="john@company.com"
            />
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              id="phone"
              v-model="formData.phone"
              type="tel"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <!-- Company -->
          <div>
            <label for="company" class="block text-sm font-semibold text-gray-700 mb-2">
              Company Name
            </label>
            <input
              id="company"
              v-model="formData.company"
              type="text"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="Your Company"
            />
          </div>

          <!-- Team Size -->
          <div>
            <label for="teamSize" class="block text-sm font-semibold text-gray-700 mb-2">
              Team Size
            </label>
            <select
              id="teamSize"
              v-model="formData.teamSize"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
            >
              <option value="">Select team size</option>
              <option value="1">Just me</option>
              <option value="2-10">2-10 people</option>
              <option value="11-50">11-50 people</option>
              <option value="51-200">51-200 people</option>
              <option value="200+">200+ people</option>
            </select>
          </div>

          <!-- Content Type -->
          <div class="md:col-span-2">
            <label for="contentType" class="block text-sm font-semibold text-gray-700 mb-2">
              What type of content do you create?
            </label>
            <select
              id="contentType"
              v-model="formData.contentType"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
            >
              <option value="">Select content type</option>
              <option value="social-media">Social Media Content</option>
              <option value="youtube">YouTube Videos</option>
              <option value="marketing">Marketing & Ads</option>
              <option value="corporate">Corporate Videos</option>
              <option value="educational">Educational Content</option>
              <option value="podcasts">Podcasts</option>
              <option value="mixed">Mixed Content</option>
              <option value="other">Other</option>
            </select>
          </div>

          <!-- Message -->
          <div class="md:col-span-2">
            <label for="message" class="block text-sm font-semibold text-gray-700 mb-2">
              Tell us about your project
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              rows="4"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
              placeholder="What are you looking to achieve with Peachy Pro Studios?"
            ></textarea>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p class="text-red-700 text-sm">{{ error }}</p>
        </div>

        <!-- Submit Button -->
        <div class="mt-8">
          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="loading">Submitting...</span>
            <span v-else>Request Demo</span>
          </button>
        </div>

        <p class="mt-6 text-center text-sm text-gray-500">
          By submitting this form, you agree to our <NuxtLink to="/privacy-policy" class="text-red-600 hover:text-red-700">Privacy Policy</NuxtLink> and <NuxtLink to="/terms" class="text-red-600 hover:text-red-700">Terms of Service</NuxtLink>.
        </p>
      </form>
    </div>
  </div>
</template>

<script lang="ts" setup>
definePageMeta({
});

const formData = ref({
  fullName: '',
  email: '',
  company: '',
  phone: '',
  teamSize: '',
  contentType: '',
  message: '',
});

const loading = ref(false);
const error = ref('');
const submitted = ref(false);

const handleSubmit = async () => {
  error.value = '';
  loading.value = true;

  try {
    const response = await $fetch('/api/demo-requests', {
      method: 'POST',
      body: formData.value,
    });

    if (response.success) {
      submitted.value = true;
      // Reset form
      formData.value = {
        fullName: '',
        email: '',
        company: '',
        phone: '',
        teamSize: '',
        contentType: '',
        message: '',
      };
    }
  } catch (err: any) {
    error.value = err.data?.statusMessage || 'Failed to submit demo request. Please try again.';
    console.error('Error submitting demo request:', err);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}
</style>
